import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

// Decouples the "Take a tour" button (in the top nav) from the tour execution
// (which happens on the Home page, where the app state lives). The button sets
// a pending signal; HomePage observes it and launches the driver.js tour.
interface TourContextValue {
  tourRequested: boolean;
  requestTour: () => void;
  consumeTourRequest: () => void;
}

const TourContext = createContext<TourContextValue | undefined>(undefined);

export function TourProvider({ children }: { children: ReactNode }) {
  const [tourRequested, setTourRequested] = useState(false);
  const requestTour = useCallback(() => setTourRequested(true), []);
  const consumeTourRequest = useCallback(() => setTourRequested(false), []);
  const value = useMemo(
    () => ({ tourRequested, requestTour, consumeTourRequest }),
    [tourRequested, requestTour, consumeTourRequest]
  );
  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within <TourProvider>');
  return ctx;
}
